// [진료 끝 ▾] / [장착 완료] — 「진료 후 질문」 트랙 시작 (2026-10-06, 플래그 roadmapEngine strict — 부모가 게이트한 뒤에만 그린다)
// 계약 docs/계약_질문로드맵_웹_2026-10-06.md §2 · 설계 §1-1.
//  - 유형 목록·라벨·잠금 = getRoadmapSettings 응답 catalog(서버 정본). 2차는 보철 4개(기준일 = 장착일)만 활성, 나머지 "준비 중".
//  - 동의 없는 환자(consents.aftercare.agreed !== true) = 버튼 비활성 + T-17 사실 문구(설득 문구 금지). 서버도 같은 조건으로 거부·저장 0.
//  - 진행 중·종료 트랙 목록 = listPatientCareTracks(계약 §6, 접속기록 track_list) — 새로고침해도 유지. [취소] = 대표만(cancelCareTrack, 응답 canCancel).
//  - 진료 유형 이름은 환자 상세에서만 허용(목록·수신함에는 내보내지 않는다).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { unifiedStyles as U } from '../../styles/Styles';
import {
    getRoadmapSettings, startCareTrack, cancelCareTrack, listPatientCareTracks, describeRoadmapError,
    CONSENT_OFF_TEXT, PROSTHESIS_TRACK_KEYS, BASIS_LOOKBACK_DAYS, todayKey, addDaysKey, fmtDayKey,
} from '../../services/careRoadmapApi';

const LOCKED_LABEL = '준비 중';
const FIT_DONE = '장착 완료';
const END_OF_VISIT = '진료 끝';

export function CareTrackStart({ pid, name, consentOn, isOwner, orderId, onStarted }) {
    const [cat, setCat] = useState(null);          // getRoadmapSettings 응답 catalog
    const [settings, setSettings] = useState(null);
    const [limits, setLimits] = useState(null);
    const [loadErr, setLoadErr] = useState('');
    const [open, setOpen] = useState(false);
    const [chosen, setChosen] = useState(null);    // catalog 항목
    const [basisDay, setBasisDay] = useState(todayKey());
    const [busy, setBusy] = useState('');
    const [error, setError] = useState('');
    const [notice, setNotice] = useState([]);      // 응답 안내 줄(계약 §2)
    const [labels, setLabels] = useState(null);    // getRoadmapSettings 응답 labels(consentOff 등 — 서버 정본, 없으면 상수)
    const [tracksResp, setTracksResp] = useState(null);   // listPatientCareTracks 응답
    const [tracksErr, setTracksErr] = useState('');

    useEffect(() => {
        let alive = true;
        getRoadmapSettings()
            .then(r => { if (!alive) return; setCat(Array.isArray(r?.catalog) ? r.catalog : []); setSettings(r?.settings || null); setLimits(r?.limits || null); setLabels(r?.labels || null); })
            .catch(e => { if (alive) { setCat([]); setLoadErr(describeRoadmapError(e, '진료 유형 목록을 불러오지 못했습니다.')); } });
        return () => { alive = false; };
    }, []);

    // 트랙 목록 — 시작·취소 뒤 다시 읽는다. 실패는 화면에 표시(조용한 실패 금지)
    const loadTracks = useCallback(async () => {
        setTracksErr('');
        try { setTracksResp(await listPatientCareTracks(pid)); }
        catch (e) { setTracksErr(describeRoadmapError(e, '질문 일정을 불러오지 못했습니다.')); }
    }, [pid]);
    useEffect(() => { loadTracks(); }, [loadTracks]);

    const lookback = limits?.basisLookbackDays || BASIS_LOOKBACK_DAYS;
    const minDay = addDaysKey(todayKey(), -lookback);
    const maxDay = todayKey();

    const items = useMemo(() => {
        const list = (cat || []).map(t => ({
            ...t,
            active: !t.locked && (t.phase === 2 || PROSTHESIS_TRACK_KEYS.includes(t.key)),
            enabledInSettings: settings?.tracks?.[t.key]?.enabled !== false,
        }));
        return [...list.filter(t => t.active), ...list.filter(t => !t.active)];
    }, [cat, settings]);

    const pickTrack = (t) => {
        setOpen(false); setError(''); setNotice([]);
        setChosen(t); setBasisDay(todayKey());
    };

    const start = async () => {
        if (!chosen || busy) return;
        if (basisDay < minDay || basisDay > maxDay) { setError(`기준일은 오늘부터 ${lookback}일 전까지입니다`); return; }
        setBusy('start'); setError(''); setNotice([]);
        try {
            const r = await startCareTrack({ pid, track: chosen.key, basisDay, orderId });
            const lines = [];
            if (r.already) lines.push('오늘 이미 시작한 일정입니다');
            if (r.firstDayKey) lines.push(`첫 질문 예정일 ${fmtDayKey(r.firstDayKey)}`);
            if (r.recallsEnded > 0) lines.push(`진행 중이던 재내원 안내 ${r.recallsEnded}건이 종료되었습니다`);
            if (r.notifyOff) lines.push('환자가 알림을 꺼 두어 질문이 전달되지 않을 수 있습니다');
            setNotice(lines);
            setChosen(null);
            await loadTracks();
            if (typeof onStarted === 'function') onStarted(r);
        } catch (e) {
            setError(describeRoadmapError(e, '질문 일정을 시작하지 못했습니다.'));
            await loadTracks();   // track-active 등 — 서버 목록을 다시 읽어 진행 중 일정을 보여준다
        } finally { setBusy(''); }
    };

    const cancel = async (t) => {
        if (!canCancel || busy) return;
        if (!window.confirm(`${t.label || ''} 질문 일정을 종료할까요? 예정된 질문은 더 보내지 않습니다.`)) return;
        setBusy(t.trackId); setError(''); setNotice([]);
        try {
            const r = await cancelCareTrack({ pid, trackId: t.trackId });
            setNotice([r.already ? `이미 종료된 일정입니다 (${r.status || ''})` : `질문 일정을 종료했습니다${r.cancelledQuestions > 0 ? ` · 예정 질문 ${r.cancelledQuestions}건 취소` : ''}`]);
            await loadTracks();
        } catch (e) {
            setError(describeRoadmapError(e, '질문 일정을 종료하지 못했습니다.'));
        } finally { setBusy(''); }
    };

    // 동의 판정: 레코드(consents.aftercare.agreed) + 서버 목록 응답 consent(해시·2년 경과까지 본 판정) — 둘 다 참일 때만 활성
    const consentEffective = consentOn && (tracksResp ? tracksResp.consent === true : true);
    const consentOffText = tracksResp?.consentOffLabel || labels?.consentOff || CONSENT_OFF_TEXT;
    const canCancel = tracksResp ? tracksResp.canCancel === true : isOwner;
    const tracks = Array.isArray(tracksResp?.items) ? tracksResp.items : [];
    const statusText = (st) => st === 'active' ? '진행 중' : st === 'cancelled' ? '종료됨' : st === 'done' ? '완료' : (st || '');
    const disabled = !consentEffective || !cat || !!busy;
    const smallBtn = { ...U.buttonSecondary, padding: '3px 10px', fontSize: 12 };

    return (
        <div style={{ marginTop: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', position: 'relative' }}>
                <button type="button" disabled={disabled} onClick={() => setOpen(o => !o)} aria-haspopup="menu" aria-expanded={open}
                    style={{ ...U.buttonSecondary, opacity: disabled ? 0.55 : 1, cursor: disabled ? 'default' : 'pointer' }}>
                    {END_OF_VISIT} <ChevronDown size={14} />
                </button>
                {!consentEffective && <span style={{ fontSize: 12, color: '#64748b' }}>{consentOffText}</span>}
                {consentEffective && !cat && !loadErr && <span style={{ fontSize: 12, color: '#94a3b8' }}>불러오는 중...</span>}
                {open && (
                    <div role="menu" style={{ position: 'absolute', top: '100%', left: 0, zIndex: 20, marginTop: 4, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', minWidth: 220, padding: 4 }}>
                        {items.length === 0 && <div style={{ padding: '8px 10px', fontSize: 12, color: '#94a3b8' }}>표시할 진료 유형이 없습니다.</div>}
                        {items.map(t => {
                            const can = t.active && t.enabledInSettings;
                            return (
                                <button key={t.key} type="button" role="menuitem" disabled={!can} onClick={() => can && pickTrack(t)}
                                    style={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '7px 10px', border: 'none', background: 'transparent', textAlign: 'left', fontSize: 13, color: can ? '#0f172a' : '#94a3b8', cursor: can ? 'pointer' : 'default', borderRadius: 6 }}>
                                    <span>{t.label}</span>
                                    {!t.active && <span style={{ ...U.badge, background: '#f1f5f9', color: '#64748b', fontSize: 11 }}>{LOCKED_LABEL}</span>}
                                    {t.active && !t.enabledInSettings && <span style={{ ...U.badge, background: '#f1f5f9', color: '#64748b', fontSize: 11 }}>설정에서 꺼짐</span>}
                                    {can && t.basis === 'fit_day' && <span style={{ fontSize: 11, color: '#64748b' }}>{FIT_DONE}</span>}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            {chosen && (
                <div style={{ marginTop: 8, border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', background: '#f8fafc' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', marginBottom: 6 }}>{chosen.label} · {chosen.basis === 'fit_day' ? FIT_DONE : END_OF_VISIT}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <label style={{ fontSize: 12.5, color: '#334155' }}>{chosen.basis === 'fit_day' ? '장착일' : '진료일'}</label>
                        <input type="date" value={basisDay} min={minDay} max={maxDay} onChange={(e) => setBasisDay(e.target.value)}
                            style={{ padding: '5px 8px', fontSize: 13, border: '1px solid #e2e8f0', borderRadius: 6 }} />
                        <span style={{ fontSize: 12, color: '#94a3b8' }}>{`기본 오늘 · ${lookback}일 전까지`}</span>
                        <button type="button" disabled={!!busy} onClick={start} style={U.buttonPrimary}>{busy === 'start' ? '시작 중...' : (chosen.basis === 'fit_day' ? FIT_DONE : END_OF_VISIT)}</button>
                        <button type="button" disabled={!!busy} onClick={() => setChosen(null)} style={smallBtn}><X size={12} /> 취소</button>
                    </div>
                </div>
            )}

            {loadErr && <div style={{ fontSize: 12.5, color: '#b91c1c', marginTop: 6 }}>{loadErr}</div>}
            {error && <div style={{ background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginTop: 8 }}>{error}</div>}
            {notice.length > 0 && (
                <div style={{ background: '#f1f5f9', color: '#334155', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginTop: 8, lineHeight: 1.6 }}>
                    {notice.map((l, i) => <div key={i}>{l}</div>)}
                </div>
            )}

            {tracksErr && <div style={{ fontSize: 12.5, color: '#b91c1c', marginTop: 6 }}>{tracksErr} <button type="button" onClick={loadTracks} style={smallBtn}>다시 시도</button></div>}
            {tracks.length > 0 && (
                <div style={{ marginTop: 8 }}>
                    {/* 진료 유형 이름(label)은 환자 상세에서만 표시 — 목록·수신함에는 내보내지 않는다(설계 §1-4) */}
                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>질문 일정</div>
                    {tracks.map(t => (
                        <div key={t.trackId} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '4px 0', flexWrap: 'wrap' }}>
                            <span style={{ fontWeight: 600, color: '#0f172a' }}>{t.label || ''}</span>
                            <span style={{ ...U.badge, background: t.status === 'active' ? '#eef2ff' : '#f1f5f9', color: t.status === 'active' ? '#4338ca' : '#64748b' }}>{statusText(t.status)}</span>
                            {t.basisDay && <span style={{ fontSize: 12, color: '#64748b' }}>{`기준일 ${fmtDayKey(t.basisDay)}`}</span>}
                            {Number.isInteger(t.stepsTotal) && t.stepsTotal > 0 && <span style={{ fontSize: 12, color: '#64748b' }}>{`${Math.min((t.stepIndex || 0) + (t.status === 'active' ? 1 : 0), t.stepsTotal)}/${t.stepsTotal}단계`}</span>}
                            {t.status === 'active' && t.nextDayKey && <span style={{ fontSize: 12, color: '#64748b' }}>{`다음 질문 예정일 ${fmtDayKey(t.nextDayKey)}`}</span>}
                            {t.status !== 'active' && t.endedAt && <span style={{ fontSize: 12, color: '#94a3b8' }}>{`종료 ${fmtDayKey(todayKey(new Date(t.endedAt)))}`}</span>}
                            {t.status === 'active' && (canCancel
                                ? <button type="button" disabled={!!busy} onClick={() => cancel(t)} style={smallBtn}>{busy === t.trackId ? '종료 중...' : '취소'}</button>
                                : <span style={{ fontSize: 12, color: '#94a3b8' }}>종료는 치과 대표만 할 수 있습니다</span>)}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// 접수 목록 → 환자 고르기 뒤 띄우는 모달 판 (환자 상세와 같은 흐름)
export function CareTrackStartModal({ pid, name, consentOn, isOwner, onClose }) {
    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
            <div style={{ ...U.card, width: '100%', maxWidth: 520 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{name ? `${name} 환자 · ${END_OF_VISIT}` : END_OF_VISIT}</h3>
                    <button type="button" onClick={onClose} style={{ ...U.buttonSecondary, padding: '4px 8px' }}><X size={14} /></button>
                </div>
                <div style={{ fontSize: 12.5, color: '#64748b' }}>진료 유형을 고르면 환자 앱으로 날짜별 질문이 나갑니다. 보철은 장착일을 기준으로 합니다.</div>
                <CareTrackStart pid={pid} name={name} consentOn={consentOn} isOwner={isOwner} />
            </div>
        </div>
    );
}

export default CareTrackStart;
